// /src/native/goodbye-notifier.ts
import { App } from '@capacitor/app';
import {
  LocalNotifications,
  PermissionStatus,
  ScheduleOptions,
} from '@capacitor/local-notifications';

export type GoodbyeMessage = { title: string; body: string };

// Swap in your real copy deck here (or import from JSON)
export const DEFAULT_GOODBYES: GoodbyeMessage[] = [
  { title: 'See you soon 👋', body: 'We’ll have a fresh adventure when you’re back.' },
  { title: 'Trauvo tip', body: 'Try a golden-hour walk nearby tonight.' },
  { title: 'Day counter rolling 📅', body: 'Keep the streak going—open Trauvo tomorrow.' },
];

type InitOpts = {
  delaysMinutes?: number[];   // candidates to pick from
  messages?: GoodbyeMessage[];// pool of messages
  dailyCap?: number;          // max sends per day
  scheduleCount?: number;     // how many to queue per background
};

const KEY_PREFIX = 'trauvo_goodbye_count_';

function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${KEY_PREFIX}${y}${m}${day}`;
}

async function getDailyCount(): Promise<number> {
  try { return Number(localStorage.getItem(todayKey()) || '0'); } catch { return 0; }
}

async function bumpDailyCount(n = 1) {
  try {
    const c = (await getDailyCount()) + n;
    localStorage.setItem(todayKey(), String(c));
  } catch {}
}

async function ensurePermission(): Promise<boolean> {
  let perm: PermissionStatus = await LocalNotifications.checkPermissions();
  if (perm.display !== 'granted') {
    perm = await LocalNotifications.requestPermissions();
  }
  return perm.display === 'granted';
}

function pickOne<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

let scheduledIds: number[] = [];

export async function initGoodbyeNotifications(opts: InitOpts = {}) {
  const delays = opts.delaysMinutes ?? [5, 2, 8, 10, 5];
  const messages = opts.messages ?? DEFAULT_GOODBYES;
  const dailyCap = Math.max(0, opts.dailyCap ?? 2);
  const scheduleCount = Math.max(1, opts.scheduleCount ?? 1);

  // Optional: nicer Android channel (no-op on iOS)
  try {
    // @ts-ignore: createChannel exists on Android
    await (LocalNotifications as any).createChannel?.({
      id: 'trauvo',
      name: 'Trauvo',
      description: 'Reminders and tips',
      importance: 3,
      visibility: 1,
    });
  } catch {}

  App.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      // User returned → cancel any pending "goodbye" pings
      if (scheduledIds.length) {
        try {
          await LocalNotifications.cancel({
            notifications: scheduledIds.map((id) => ({ id })),
          });
        } catch {}
        scheduledIds = [];
      }
      return;
    }

    // Going to background → schedule a random nudge
    const ok = await ensurePermission();
    if (!ok) return;

    const sentToday = await getDailyCount();
    if (sentToday >= dailyCap) return;

    const now = Date.now();
    const notifications: NonNullable<ScheduleOptions['notifications']> = [];

    for (let i = 0; i < scheduleCount && sentToday + i < dailyCap; i++) {
      const delayMin = pickOne(delays);
      const msg = pickOne(messages);
      const id = Math.floor(now % 1_000_000) + i;

      notifications.push({
        id,
        title: msg.title,
        body: msg.body,
        schedule: { at: new Date(now + delayMin * 60_000) },
        // channelId for Android if created above
        extra: { source: 'trauvo-goodbye' },
      });
      scheduledIds.push(id);
    }

    if (notifications.length) {
      try {
        await LocalNotifications.schedule({ notifications });
        await bumpDailyCount(notifications.length);
      } catch (e) {
        console.warn('[Trauvo] schedule goodbye failed', e);
      }
    }
  });
}
